import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
} from "react";
import Timeline from "./Timeline";
import { TimelineHeader } from "./TimelineHeader";
import Sidebar from "./Sidebar";

import { startOfYear, differenceInDays, getMonth, getWeek } from "date-fns";
import { SharedTimelineContext } from "../SharedTimelineContext";
import { Rnd } from "react-rnd";
import { getCookie, setCookie } from "../../../helpers/cookieUtils";
import moment from "moment";
import { getMonthsInRange,
  getNumberOfWeeksBetweenYears,
  getDaysBetweenYears,
  getTodayPositionForYearRange, 
  getNumberOfQuartersBetweenYears,
  getNumberOfYearsBetweenYears} 
  from "../../dashboard/utils/date";
import { calculateDynamicWidths } from "../utils/dynamicWidth";
import { TargetDateEventsRow } from "../components/TargetDateEventsRow";
import CreateEditTask from "../../scopeOverview/CreateEditTask";
import { KEY_EVENT_TYPES } from "../constants";

export const GanttChartContainer = ({
  viewType = "month",
  isHide,
  count,
  isFullHeight,
  entityType,
  keyEvents = [],
}) => {
  const {
    displayYearRange,
    todayTrigger,
    timelineData,
    fetchAndSetTimelineData,
    entityID,
    expandedProjects,
    expandedScopes,
    expandedTaskGroups,
    toggleProject,
    toggleScope,
    toggleTaskGroup,
  } = useContext(SharedTimelineContext);

  const showKeyEventsRow = entityType === "portfolio" || entityType === "program" || entityType === "projectTimeline";

  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [clickedKeyEvents, setClickedKeyEvents] = useState([]);
  const [taskModal, setTaskModal] = useState({ isOpen: false, taskId: null, scopeID: null, scopeName: null, projectID: null, projectName: null });

  const handleKeyEventClick = (eventIds) => {
    setClickedKeyEvents(eventIds);
  };

  const handleTaskClick = ({ taskId, scopeID, scopeName, projectID, projectName }) => {
    setTaskModal({ isOpen: true, taskId, scopeID, scopeName, projectID, projectName });
  };

  const closeTaskModal = () => {
    setTaskModal({ isOpen: false, taskId: null, scopeID: null, scopeName: null, projectID: null, projectName: null });
  };

  const containerRef = useRef(null);
  const sidebarRef = useRef(null);
  const timelineRef = useRef(null);
  const numberOfDaysBetweenYears = getDaysBetweenYears(displayYearRange.start, displayYearRange.end);
  const defaultSidebarWidth = 450;
  const defaultColumnWidths = {
    scopes: 170,
    completed: 40,
    duration: 80,
    startDate: 80,
    endDate: 80,
  };

  const cookiePrefix = entityType === "portfolio" ? "portfolio" : "program";

  const [sidebarWidth, setSidebarWidth] = useState(
    parseInt(getCookie(`${cookiePrefix}SidebarWidth`), 10) || defaultSidebarWidth
  );
  const [columnWidths, setColumnWidths] = useState({
    scopes: parseInt(getCookie(`${cookiePrefix}_scopesColumnWidth`), 10) || 170,
    completed: parseInt(getCookie(`${cookiePrefix}_completedColumnWidth`), 10) || 40,
    duration: parseInt(getCookie(`${cookiePrefix}_durationColumnWidth`), 10) || 80,
    startDate: parseInt(getCookie(`${cookiePrefix}_startDateColumnWidth`), 10) || 80,
    endDate: 80,
  });

  function calculatedColumnWidths() {
    const totalWidth =
      columnWidths.scopes +
      columnWidths.completed +
      columnWidths.duration +
      columnWidths.startDate +
      columnWidths.endDate;
    return totalWidth;
  }
  const [isResizingHeight, setIsResizingHeight] = useState(false);
  const storedHeight = localStorage.getItem("programGanttChartHeight");

  const [componentHeight, setComponentHeight] = useState(
    isFullHeight
      ? "100vh - 240px"
      : storedHeight
        ? storedHeight
        : "calc(100vh - 450px)"
  );

  useEffect(() => {
    setComponentHeight(
      isFullHeight
        ? "100vh - 240px"
        : storedHeight
          ? storedHeight
          : "calc(100vh - 450px)"
    );
  }, [isFullHeight]);

  const ganttChartContainerRef = useRef(null);
  const scrollSyncInProgress = useRef(false);
  const months = getMonthsInRange(displayYearRange.start, displayYearRange.end);

  const [containerWidth, setContainerWidth] = useState(0);

  // Calculate dynamic widths for year view in portfolio
  const dynamicWidths = React.useMemo(() => {
    if (entityType === "portfolio" && viewType === "year" && containerWidth > 0) {
      const effectiveSidebarWidth = isHide ? 0 : sidebarWidth;
      return calculateDynamicWidths(viewType, containerWidth, displayYearRange, effectiveSidebarWidth);
    }
    return { useDynamic: false };
  }, [entityType, viewType, containerWidth, displayYearRange, sidebarWidth, isHide]);

  const totalWidth = {
    month: months.length * 241,
    quarter: getNumberOfQuartersBetweenYears(displayYearRange.start, displayYearRange.end) * 501,
    year: dynamicWidths.useDynamic && entityType === "portfolio"
      ? dynamicWidths.totalWidth
      : getNumberOfYearsBetweenYears(displayYearRange.start, displayYearRange.end) * 501,
    week: getNumberOfWeeksBetweenYears(displayYearRange.start, displayYearRange.end) * 241,
    day: numberOfDaysBetweenYears * 241,
  };

  // Measure container width on mount and resize
  useEffect(() => {
    const measureWidth = () => {
      if (ganttChartContainerRef.current) {
        const width = ganttChartContainerRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    measureWidth();

    const resizeObserver = new ResizeObserver(measureWidth);
    if (ganttChartContainerRef.current) {
      resizeObserver.observe(ganttChartContainerRef.current);
    }

    window.addEventListener('resize', measureWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureWidth);
    };
  }, []);

  // Recalculate on sidebar width or isHide change
  useEffect(() => {
    if (ganttChartContainerRef.current) {
      const width = ganttChartContainerRef.current.offsetWidth;
      setContainerWidth(width);
    }
  }, [sidebarWidth, isHide]);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    const timeline = timelineRef.current;
    const container = containerRef.current;

    if (!container || !sidebar || !timeline) return;

    const handleHorizontalScroll = (e) => {
      const scrollLeft = e.target.scrollLeft;
      container.scrollLeft = scrollLeft;
    };

    const handleVerticalScroll = (e) => {
      if (scrollSyncInProgress.current) return;

      scrollSyncInProgress.current = true;
      const scrollTop = e.target.scrollTop;

      if (e.target === sidebar && timeline.scrollTop !== scrollTop) {
        timeline.scrollTop = scrollTop;
      } else if (e.target === timeline && sidebar.scrollTop !== scrollTop) {
        sidebar.scrollTop = scrollTop;
      }

      setTimeout(() => {
        scrollSyncInProgress.current = false;
      }, 10);
    };

    container.addEventListener("scroll", handleHorizontalScroll);
    sidebar.addEventListener("scroll", handleVerticalScroll, { passive: true });
    timeline.addEventListener("scroll", handleVerticalScroll, {
      passive: true,
    });

    return () => {
      container.removeEventListener("scroll", handleHorizontalScroll);
      sidebar.removeEventListener("scroll", handleVerticalScroll);
      timeline.removeEventListener("scroll", handleVerticalScroll);
    };
  }, []);

   useLayoutEffect(() => {
      const calculateScrollPosition = () => {
        if (!containerRef.current) return;
        containerRef.current.scrollLeft = getTodayPositionForYearRange(viewType, displayYearRange?.start, displayYearRange?.end) - 300;
      };
      setTimeout(calculateScrollPosition, 100);
    }, [viewType, count, todayTrigger]);

  const resizeHeight = (e) => {
    if (isResizingHeight && ganttChartContainerRef.current) {
      const containerTop =
        ganttChartContainerRef.current.getBoundingClientRect().top;
      const minHeight = 266;
      const maxHeight = window.innerHeight - containerTop - 20;
      const newHeight = Math.min(
        maxHeight,
        Math.max(minHeight, e.clientY - containerTop)
      );
      setComponentHeight(() => newHeight + "px");
      localStorage.setItem("programGanttChartHeight", newHeight + "px");
    }
  };

  useEffect(() => {
    if (!isResizingHeight) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      resizeHeight(e);
    };

    const handleMouseUp = () => {
      setIsResizingHeight(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingHeight]);

  const startHeightResizing = (e) => {
    e.preventDefault();
    setIsResizingHeight(true);
  };

  const getColumnHeaderText = () => {
    if (entityType === "portfolio") {
      return "Programs / Projects";
    }
    if (entityType === "projectTimeline") {
      return "Scope Items / Tasks";
    }
    return "Projects / Scope Items";
  };

  // Filter events for the Key Events row: all TARGET_DATE events (any link status), plus DATE_RANGE
  // events with no row of their own to render a bar on - either unlinked (Global), or linked straight
  // back to the entity being viewed (a shared-down event whose target IS this entity, not one of its
  // descendants - the tree only has nodes for descendants, never for the entity itself, so
  // KeyEventMarkers can never find a row for these and would silently drop them).
  const targetDateEvents = React.useMemo(() => {
    if (showKeyEventsRow && keyEvents && keyEvents.length > 0) {
      return keyEvents.filter(event => event.type === KEY_EVENT_TYPES.TARGET_DATE || !event.linkRefId || event.linkRefId === entityID);
    }
    return [];
  }, [showKeyEventsRow, keyEvents, entityID]);

  return (
    <>
    <div
      className="relative flex w-full"
      ref={ganttChartContainerRef}
      style={{ height: `calc(${componentHeight})` }}
    >
      {!isHide && (
        <div
          className="relative flex border-r border-[#342c82] w-full min-h-[calc(100vh-450px)] overflow-hidden"
          style={{
            height: `calc(${componentHeight})`,
          }}
        >
          <Rnd
            default={{
              width: sidebarWidth,
            }}
            size={{ width: sidebarWidth }}
            bounds="window"
            enableResizing={{ right: true }}
            style={{
              position: "sticky",
              left: 0,
              cursor: "pointer",
            }}
            onResizeStop={(e, direction, ref) => {
              const newWidth = Number(ref.style.width.replace("px", ""));
              setSidebarWidth(newWidth);
              setCookie(`${cookiePrefix}SidebarWidth`, newWidth);
            }}
          >
            <div className="flex flex-col w-full overflow-x-auto">
              <div
                className="h-[36px] bg-[#ece7fd] border-b border-[#342c82] flex items-center overflow-x-auto"
                style={{
                  minWidth: calculatedColumnWidths(),
                }}
              >
                <Rnd
                  default={{
                    width: columnWidths.scopes,
                  }}
                  size={{ width: columnWidths.scopes }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      scopes: newWidth > 170 ? newWidth : 170,
                    });
                    setCookie(
                      `${cookiePrefix}_scopesColumnWidth`,
                      newWidth > 170 ? newWidth : 170
                    );
                  }}
                >
                  <p
                    className="h-[34px] flex justify-start items-center font-medium text-sm truncate pl-4"
                    style={{
                      width: columnWidths.scopes,
                    }}
                  >
                    {getColumnHeaderText()}
                  </p>
                </Rnd>
                <Rnd
                  default={{
                    width: columnWidths.completed,
                  }}
                  size={{ width: columnWidths.completed }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      completed: newWidth > 40 ? newWidth : 40,
                    });
                    setCookie(
                      `${cookiePrefix}_completedColumnWidth`,
                      newWidth > 40 ? newWidth : 40
                    );
                  }}
                >
                  <p
                    className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate"
                    style={{
                      minWidth: columnWidths.completed,
                    }}
                  >
                    (%)
                  </p>
                </Rnd>
                <Rnd
                  default={{
                    width: columnWidths.duration,
                  }}
                  size={{ width: columnWidths.duration }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      duration: newWidth > 80 ? newWidth : 80,
                    });
                    setCookie(
                      `${cookiePrefix}_durationColumnWidth`,
                      newWidth > 80 ? newWidth : 80
                    );
                  }}
                >
                  <p
                    className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate "
                    style={{
                      minWidth: columnWidths.duration,
                    }}
                  >
                    Duration
                  </p>
                </Rnd>
                <Rnd
                  default={{
                    width: columnWidths.startDate,
                  }}
                  size={{ width: columnWidths.startDate }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      startDate: newWidth > 80 ? newWidth : 80,
                    });
                    setCookie(
                      `${cookiePrefix}_startDateColumnWidth`,
                      newWidth > 80 ? newWidth : 80
                    );
                  }}
                >
                  <p
                    className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate "
                    style={{
                      minWidth: columnWidths.startDate,
                    }}
                  >
                    Start Date
                  </p>
                </Rnd>
                <p
                  className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate "
                  style={{
                    minWidth: columnWidths.endDate,
                  }}
                >
                  End Date
                </p>
              </div>
              
              {viewType !== "day" && (
                <div
                  className="h-[24px] border-b border-[#342c82] bg-[#ece7fd]"
                  style={{ minWidth: calculatedColumnWidths() }}
                ></div>
              )}
              {showKeyEventsRow && viewType !== "day" && (
                <div
                  className="h-[40px] bg-[#f9f9fb] border-b-2 border-[#342c82] flex items-center px-4"
                  style={{
                    minWidth: calculatedColumnWidths(),
                  }}
                >
                  <span className="font-semibold text-sm text-[#342C82]">
                    Key Events
                  </span>
                </div>
              )}
              <Sidebar
                reference={sidebarRef}
                viewType={viewType}
                componentHeight={componentHeight}
                expandedProjects={expandedProjects}
                toggleProject={toggleProject}
                entityType={entityType}
                sidebarWidth={calculatedColumnWidths()}
                columnWidths={columnWidths}
                hoveredRowId={hoveredRowId} 
                setHoveredRowId={setHoveredRowId}
                expandedScopes={expandedScopes}
                toggleScope={toggleScope}
                expandedTaskGroups={expandedTaskGroups}
                toggleTaskGroup={toggleTaskGroup}
                onTaskClick={handleTaskClick}
              />
            </div>
          </Rnd>
        </div>
      )}

      <div
        style={{
          width: !isHide ? `calc(100% - ${sidebarWidth}px)` : "100%",
          display: "flex",
          position: "sticky",
        }}
      >
        <div ref={containerRef} className="overflow-x-auto overflow-y-hidden">
          <div style={{ width: `${totalWidth[viewType]}px` }}>
            <div
              className={`${(viewType === "week" || viewType === "quarter" || viewType === "year" ) ? "h-[60px]" : "h-[36px]"
                } bg-[#ece7fd] flex border-b border-[#342c82]`}
            >
              <TimelineHeader
                viewType={viewType}
                displayYearRange={displayYearRange}
                dynamicWidths={dynamicWidths}
              />
            </div>
            {viewType === "month" && (
              <div className="h-[24px] bg-[#ece7fd] border-b border-[#342c82]">
                <TimelineHeader
                  viewType={viewType}
                  subHeader={true}
                  displayYearRange={displayYearRange}
                  dynamicWidths={dynamicWidths}
                />
              </div>
            )}
            {showKeyEventsRow && viewType !== "day" && (
              <TargetDateEventsRow
                events={targetDateEvents}
                viewType={viewType}
                displayYearRange={displayYearRange}
                dynamicWidths={dynamicWidths}
                timelineData={timelineData}
                expandedProjects={expandedProjects}
                onEventClick={handleKeyEventClick}
              />
            )}
            <Timeline
              reference={timelineRef}
              viewType={viewType}
              componentHeight={componentHeight}
              isHide={isHide}
              expandedProjects={expandedProjects}
              entityType={entityType}
              displayYearRange={displayYearRange}
              hoveredRowId={hoveredRowId} 
              setHoveredRowId={setHoveredRowId}
              keyEvents={keyEvents}
              dynamicWidths={dynamicWidths}
              expandedScopes={expandedScopes}
              expandedTaskGroups={expandedTaskGroups}
              onTaskClick={handleTaskClick}
            />
          </div>
        </div>
      </div>
    </div>

    {taskModal.isOpen && (
      <CreateEditTask
        isOpen={taskModal.isOpen}
        isEdit={true}
        taskId={taskModal.taskId}
        scopeID={taskModal.scopeID}
        scopeName={taskModal.scopeName}
        projectID={taskModal.projectID}
        projectName={taskModal.projectName}
        onClose={closeTaskModal}
        onSave={() => {
          closeTaskModal();
          fetchAndSetTimelineData();
        }}
      />
    )}
  </>
  );
};
